/* @jsxImportSource react */
'use client';

import React from 'react';

import { cn } from '../../utils/cn';
import { type StripUndesiredProps } from '../../utils/strip-undesired-props';

/** Common DPR values; any positive number is accepted. */
export type ImageDensity = 1 | 2 | 3 | (number & {});

/** Image MIME types browsers can decode; any string is accepted. */
export type ImageMimeType =
  | 'image/avif'
  | 'image/webp'
  | 'image/jpeg'
  | 'image/png'
  | 'image/gif'
  | 'image/svg+xml'
  | 'image/apng'
  | 'image/bmp'
  | 'image/x-icon'
  | (string & {});

/**
 * - `string` — pre-formatted, e.g. `"img-800.jpg 800w"`
 * - `{ src, width }` — width descriptor, pair with `sizes`
 * - `{ src, density }` — density descriptor, for fixed render size
 */
export type ImageSrcSetEntry =
  | string
  | { src: string; width: number }
  | { src: string; density: ImageDensity };

export interface ImagePictureSource {
  srcSet: string | ImageSrcSetEntry[];
  type?: ImageMimeType;
  media?: string;
  sizes?: string;
  width?: number;
  height?: number;
}

function formatSrcSet(entries: string | ImageSrcSetEntry[] | undefined): string | undefined {
  if (entries == null) return undefined;
  if (typeof entries === 'string') return entries;
  return entries
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if ('width' in entry) return `${entry.src} ${entry.width}w`;
      return `${entry.src} ${entry.density}x`;
    })
    .join(', ');
}

export type ImageProps = StripUndesiredProps<
  Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'srcSet'>
> & {
  /** Required. Pass `""` for purely decorative images. */
  alt: string;
  srcSet?: string | ImageSrcSetEntry[];
  /** When provided, wraps the `<img>` in a `<picture>` with these `<source>`s. */
  sources?: ImagePictureSource[];
  /** Swapped in if the primary `src` fails to load. */
  fallbackSrc?: string;
  fetchPriority?: 'high' | 'low' | 'auto';
  className?: string;
  /** Applied to the outer `<picture>` wrapper when `sources` are provided. */
  pictureClassName?: string;
};

// Builds a deterministic React key for a <source>. A <picture> can hold several
// sources that share a srcSet but differ by type, media, or sizes (art
// direction / format fallbacks), so all of those must contribute to the key to
// avoid collisions. formatSrcSet serializes srcSet entries including their
// width/density descriptors.
const PICTURE_KEY_SEPARATOR = '|';

const getPictureSrcId = (picture: ImagePictureSource): string =>
  [formatSrcSet(picture.srcSet), picture.type, picture.media, picture.sizes]
    .map((value) => value ?? '')
    .join(PICTURE_KEY_SEPARATOR);

/**
 * Defaults to `loading="lazy"` and `decoding="async"`.
 */
export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      alt,
      className,
      srcSet,
      sources,
      fallbackSrc,
      loading = 'lazy',
      decoding = 'async',
      fetchPriority,
      onError,
      pictureClassName,
      ...props
    },
    ref,
  ) => {
    const handleError = React.useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (fallbackSrc && event.currentTarget.src !== fallbackSrc) {
          event.currentTarget.src = fallbackSrc;
          // Clear srcset so the fallback isn't overridden by a re-resolved candidate.
          event.currentTarget.srcset = '';
        }
        onError?.(event);
      },
      [fallbackSrc, onError],
    );

    // onError is routed through a spread so static jsx-a11y rules (which
    // flag handlers on non-interactive elements) don't trip on a load-failure
    // hook that has nothing to do with user interaction.
    const errorProps = fallbackSrc || onError ? { onError: handleError } : undefined;
    const fetchPriorityProps = fetchPriority ? { fetchpriority: fetchPriority } : undefined;

    const img = (
      // eslint-disable-next-line atoms/no-raw-elements -- this is the Image component's own <img> implementation
      <img
        ref={ref}
        alt={alt}
        srcSet={formatSrcSet(srcSet)}
        loading={loading}
        decoding={decoding}
        className={cn('block max-w-full', className)}
        {...props}
        {...fetchPriorityProps}
        {...errorProps}
      />
    );

    if (!sources || sources.length === 0) {
      return img;
    }

    return (
      // eslint-disable-next-line atoms/no-raw-elements -- Image renders the <picture> wrapper when `sources` is passed
      <picture className={pictureClassName}>
        {sources.map((source) => (
          <source
            key={getPictureSrcId(source)}
            srcSet={formatSrcSet(source.srcSet)}
            type={source.type}
            media={source.media}
            sizes={source.sizes}
            width={source.width}
            height={source.height}
          />
        ))}
        {img}
      </picture>
    );
  },
);

Image.displayName = 'Image';
